import React, { useEffect, useState } from 'react';
import { usePortal } from '../../contexts/PortalContext';
import { EMPTY_WRAP_UP, fetchWrapUp, hasWrapUpContent, saveWrapUp, type WrapUp, type WrapUpGroup } from '../../utils/wrapUp';
import { AREA, Block, ROW, RowRemove, Switch } from './WrapUpParts';
import { WrapUpPhotoBlock } from './WrapUpPhotoBlock';
import { BUTTON_GHOST, BUTTON_QUIET, BUTTON_SOLID, FIELD } from './adminStyles';

/**
 * THE WRAP-UP EDITOR — where the post-show report is written.
 *
 * Until this existed the feature was a one-off dressed up as a system: ATM's
 * report reached the client page because it was typed into the database by
 * hand.
 *
 * **IT TAKES A PROJECT ID RATHER THAN READING THE OPEN ONE.** It began as a
 * section on Project Admin, where `activeProject` was to hand. Tom could not
 * find it there — *"I don't want it under the projects - it's not easy to
 * find"* — so it moved to its own tab, which picks a project from a list and
 * has no open project of its own.
 *
 * **THE DEFAULT IS NOTHING.** Tom: *"Some projects simply don't require
 * anything as 'grand' as a wrap up report."* So an untouched project shows
 * one line and a button, not an empty form implying content is owed.
 *
 * **WRITING AND PUBLISHING ARE SEPARATE.** Shilpa wrote ATM's over several
 * days; content saves whether or not the switch is on. The switch is part
 * of the form rather than a control that writes on click, so publishing can
 * never send a version different from the one on screen.
 *
 * Two fields sharing a row are wrapped in sizing divs — `FIELD` carries
 * `w-full`, so a width class beside it is ignored and the row wraps.
 */

export function WrapUpEditor({
  projectId
}: {
  projectId: string;
}) {
  const {
    notify
  } = usePortal();
  const [wrap, setWrap] = useState<WrapUp>(EMPTY_WRAP_UP);
  const [loaded, setLoaded] = useState(false);
  const [opened, setOpened] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  useEffect(() => {
    let live = true;
    setLoaded(false);
    void fetchWrapUp(projectId).then(result => {
      if (!live) return;
      setWrap(result ?? EMPTY_WRAP_UP);
      setOpened(false);
      setDirty(false);
      setLoaded(true);
    });
    return () => {
      live = false;
    };
  }, [projectId]);
  function edit(patch: Partial<WrapUp>) {
    setWrap(current => ({
      ...current,
      ...patch
    }));
    setDirty(true);
  }
  function editGroups(groups: WrapUpGroup[]) {
    edit({
      next: {
        ...(wrap.next ?? {
          heading: '',
          body: '',
          groups: []
        }),
        groups
      }
    });
  }
  async function save() {
    setSaving(true);
    const result = await saveWrapUp(projectId, wrap);
    setSaving(false);
    if (!result.ok) {
      notify(`Could not save — ${result.error ?? 'the database refused the write'}`);
      return;
    }
    setDirty(false);
    notify(wrap.published ? 'Wrap-up saved · live on the client page' : 'Wrap-up saved · not shown to the client yet');
  }
  async function discard() {
    setSaving(true);
    const result = await saveWrapUp(projectId, EMPTY_WRAP_UP);
    setSaving(false);
    if (!result.ok) {
      notify(`Could not remove — ${result.error ?? 'the database refused the write'}`);
      return;
    }
    setWrap(EMPTY_WRAP_UP);
    setOpened(false);
    setDirty(false);
    setConfirmDiscard(false);
    notify('Wrap-up removed');
  }
  const started = opened || hasWrapUpContent(wrap);
  const next = wrap.next;

  /* Which seams exist in this report — the photographs are dealt only into
     sections that are actually there. */
  const sections = {
    closing: Boolean(wrap.closing.trim()),
    voices: wrap.quotes.length > 0,
    next: Boolean(next)
  };

  /* What the client sees, in words, so nobody has to publish to find out. */
  const parts: string[] = [];
  if (wrap.statement.trim()) parts.push('an opening statement');
  if (wrap.numbers.length) parts.push(`${wrap.numbers.length} figure${wrap.numbers.length === 1 ? '' : 's'}`);
  if (wrap.photos.length) parts.push(`${wrap.photos.length} photograph${wrap.photos.length === 1 ? '' : 's'}`);
  if (wrap.closing.trim()) parts.push('a closing paragraph');
  if (wrap.quotes.length) parts.push(`${wrap.quotes.length} testimonial${wrap.quotes.length === 1 ? '' : 's'}`);
  if (next) parts.push('the forward look');
  const summary = !wrap.published ? 'Off. Their page shows their films and files, nothing else.' : parts.length === 0 ? 'On, but nothing is written yet — so the page still shows films and files only.' : `On. They will see ${parts.join(', ').replace(/, ([^,]*)$/, ' and $1')}.`;
  if (!loaded) return <p className="label mt-10 text-paper/35" data-id="element-1700" data-mp="326pnohyxymk3">Loading&hellip;</p>;
  if (!started) {
    return <div className="mt-10 border border-ruleDark px-8 py-14 text-center" data-id="element-1701" data-mp="vg9rdb9x26yj">
        <p className="m-0 font-display text-2xl text-paper" data-id="element-1702" data-mp="10eusre0wmwok">No wrap-up on this project</p>
        <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-paper/40" data-id="element-1703" data-mp="1ai03o497czzo">
          Most don&rsquo;t have one. Start one only when there is a report worth showing.
        </p>
        <button type="button" onClick={() => setOpened(true)} className={`${BUTTON_GHOST} mt-8`} data-id="element-1704" data-mp="15hc5mymk3ivy">
          Start a wrap-up
        </button>
      </div>;
  }
  return <div className="mt-10" data-id="element-1705" data-mp="1eik015lybzkr">
      <div className="flex items-start gap-6 border border-ruleDark bg-charcoal px-7 py-7" data-id="element-1706" data-mp="1t8raay1t2cad">
        <Switch on={wrap.published} onChange={() => edit({
        published: !wrap.published
      })} label="Show this wrap-up on the client page" data-id="element-1707" data-mp="1takiwivzb6l0" />
        <div className="flex-grow" data-id="element-1708" data-mp="228mpddyherfo">
          <p className="m-0 text-base text-paper" data-id="element-1709" data-mp="1y83v7d9499ab">Show this wrap-up on the client page</p>
          <p className="mt-3 m-0 text-sm leading-relaxed text-paper/40" data-id="element-1710" data-mp="7veq9a3h3to8">{summary}</p>
        </div>
      </div>

      <Block title="Opening statement" filled={Boolean(wrap.statement)} addLabel="Add a statement" onAdd={() => edit({
      statement: ' '
    })} onRemove={() => edit({
      statement: ''
    })} data-id="element-1711" data-mp="3u2wqthhaoar">
        <textarea rows={2} aria-label="Opening statement" value={wrap.statement} onChange={e => edit({
        statement: e.target.value
      })} placeholder="The line the report opens with" className={AREA} data-id="element-1712" data-mp="27ypcv6n2oqr0" />
      </Block>

      <Block title="Figures" hint="Only where the numbers mean something. A single hero film with &ldquo;1 film, 1 day&rdquo; is worse than nothing." filled={wrap.numbers.length > 0} count={wrap.numbers.length ? `${wrap.numbers.length} added` : 'None'} addLabel="Add a figure" onAdd={() => edit({
      numbers: [...wrap.numbers, {
        value: '',
        label: ''
      }]
    })} data-id="element-1713" data-mp="2lxyt26ba3kuz">
        {wrap.numbers.map((figure, index) => <div key={index} className={`${ROW} flex flex-wrap items-end gap-x-6 gap-y-3`} data-id="element-1714" data-mp="35jbnot4vz46k" data-mpi={index}>
            <div className="w-24 shrink-0" data-id="element-1715" data-mp="39n9l8zekc6xi">
              <input type="text" aria-label={`Figure ${index + 1}`} value={figure.value} onChange={e => {
            const numbers = wrap.numbers.slice();
            numbers[index] = {
              ...numbers[index],
              value: e.target.value
            };
            edit({
              numbers
            });
          }} placeholder="24" className={`${FIELD} text-xl`} data-id="element-1716" data-mp="y0645s1r1k7t" />
            </div>
            <div className="min-w-[16rem] flex-grow" data-id="element-1717" data-mp="1hpgltbl22f8k">
              <input type="text" aria-label={`What figure ${index + 1} counts`} value={figure.label} onChange={e => {
            const numbers = wrap.numbers.slice();
            numbers[index] = {
              ...numbers[index],
              label: e.target.value
            };
            edit({
              numbers
            });
          }} placeholder="high-level studio interviews with industry leaders" className={FIELD} data-id="element-1718" data-mp="1cpvhsmr13goz" />
            </div>
            <RowRemove label={`Remove figure ${index + 1}`} onClick={() => edit({
          numbers: wrap.numbers.filter((_, at) => at !== index)
        })} data-id="element-1719" data-mp="13yiw8v3exp0k" />
          </div>)}
      </Block>

      <WrapUpPhotoBlock projectId={projectId} photos={wrap.photos} sections={sections} onChange={photos => edit({
      photos
    })} notify={notify} data-id="element-1720" data-mp="uxcekip1285q" />

      <Block title="Closing paragraph" hint="Sits between the figures, set large on black. It is also what keeps the two bands of figures apart." filled={Boolean(wrap.closing)} addLabel="Add a paragraph" onAdd={() => edit({
      closing: ' '
    })} onRemove={() => edit({
      closing: ''
    })} data-id="element-1721" data-mp="1moihf6w4ujyp">
        <textarea rows={3} aria-label="Closing paragraph" value={wrap.closing} onChange={e => edit({
        closing: e.target.value
      })} className={AREA} data-id="element-1722" data-mp="2a8ems4skk0t" />
      </Block>

      <Block title="Testimonials" hint="Word for word. Never trimmed to fit — shortening somebody&rsquo;s endorsement is not ours to do." filled={wrap.quotes.length > 0} count={wrap.quotes.length ? `${wrap.quotes.length} added` : 'None'} addLabel="Add a testimonial" onAdd={() => edit({
      quotes: [...wrap.quotes, {
        body: '',
        name: '',
        role: ''
      }]
    })} data-id="element-1723" data-mp="3uwnab86nhigf">
        {wrap.quotes.map((quote, index) => <div key={index} className={ROW} data-id="element-1724" data-mp="378dqucwwt6sg" data-mpi={index}>
            <div className="flex items-start gap-6" data-id="element-1725" data-mp="379ovsg23ckl5">
              <textarea rows={3} aria-label={`Testimonial ${index + 1}`} value={quote.body} onChange={e => {
            const quotes = wrap.quotes.slice();
            quotes[index] = {
              ...quotes[index],
              body: e.target.value
            };
            edit({
              quotes
            });
          }} placeholder="What they said, word for word" className={`${AREA} flex-grow`} data-id="element-1726" data-mp="3lxa20iu9izxa" />
              <RowRemove label={`Remove testimonial ${index + 1}`} onClick={() => edit({
            quotes: wrap.quotes.filter((_, at) => at !== index)
          })} data-id="element-1727" data-mp="3mthesjzwxxr1" />
            </div>
            <div className="flex flex-wrap gap-x-8" data-id="element-1728" data-mp="37ykwfdye3u08">
              <div className="min-w-[12rem] flex-grow" data-id="element-1729" data-mp="37xrq2e3cf6im">
                <input type="text" aria-label={`Name on testimonial ${index + 1}`} value={quote.name} onChange={e => {
              const quotes = wrap.quotes.slice();
              quotes[index] = {
                ...quotes[index],
                name: e.target.value
              };
              edit({
                quotes
              });
            }} placeholder="Name" className={FIELD} data-id="element-1730" data-mp="33wpohqlyeidh" />
              </div>
              <div className="min-w-[16rem] flex-grow-[2]" data-id="element-1731" data-mp="2g7n78jv8erji">
                <input type="text" aria-label={`Role on testimonial ${index + 1}`} value={quote.role} onChange={e => {
              const quotes = wrap.quotes.slice();
              quotes[index] = {
                ...quotes[index],
                role: e.target.value
              };
              edit({
                quotes
              });
            }} placeholder="Role, company" className={FIELD} data-id="element-1732" data-mp="2g86nzx95b9vy" />
              </div>
            </div>
          </div>)}
      </Block>

      <Block title="Where this could go" hint="The forward look. Clients forward this one internally, so it goes on only when there is a real plan behind it." filled={Boolean(next)} addLabel="Add a forward look" onAdd={() => edit({
      next: {
        heading: '',
        body: '',
        groups: []
      }
    })} onRemove={() => edit({
      next: null
    })} data-id="element-1733" data-mp="gzoopvl48cwq">
        {next && <div data-id="element-1734" data-mp="3uekykxm2e65x">
            <label className="block" data-id="element-1735" data-mp="3ue1r6au1j065">
              <span className="label block text-paper/40" data-id="element-1736" data-mp="3ud02v4o0kh6h">Headline</span>
              <textarea rows={2} value={next.heading} onChange={e => edit({
            next: {
              ...next,
              heading: e.target.value
            }
          })} placeholder="Moving forward, we want to take…" className={AREA} data-id="element-1737" data-mp="3md3vodt8zb16" />
            </label>

            <label className="mt-8 block" data-id="element-1738" data-mp="3r65cird0lkne">
              <span className="label block text-paper/40" data-id="element-1739" data-mp="33fj24tfjfoa8">Introduction</span>
              <textarea rows={4} value={next.body} onChange={e => edit({
            next: {
              ...next,
              body: e.target.value
            }
          })} placeholder="One or two paragraphs. Leave a blank line between them." className={AREA} data-id="element-1740" data-mp="33hm328i8b1tq" />
            </label>

            <p className="label mt-10 text-paper/40" data-id="element-1741" data-mp="snhnhlv7m273">Groups</p>
            {next.groups.map((group, gi) => <div key={gi} className="mt-5 border border-ruleDark px-6 py-6" data-id="element-1742" data-mp="np6fzhse03s1" data-mpi={gi}>
                <div className="flex items-end gap-6" data-id="element-1743" data-mp="25mjhhui7wcj3">
                  <div className="flex-grow" data-id="element-1744" data-mp="25kqbmhffhwc5">
                    <input type="text" aria-label={`Group ${gi + 1} name`} value={group.title} onChange={e => {
                const groups = next.groups.slice();
                groups[gi] = {
                  ...groups[gi],
                  title: e.target.value
                };
                editGroups(groups);
              }} placeholder="Content planning" className={`${FIELD} text-base`} data-id="element-1745" data-mp="2al4ku2srncbr" />
                  </div>
                  <RowRemove label={`Remove group ${gi + 1}`} onClick={() => editGroups(next.groups.filter((_, at) => at !== gi))} data-id="element-1746" data-mp="2fk7fctb4e05h" />
                </div>

                {group.points.map((point, pi) => <div key={pi} className="mt-4 flex items-end gap-5" data-id="element-1747" data-mp="3d0wn13mr3wms" data-mpi={pi}>
                    <div className="flex-grow" data-id="element-1748" data-mp="1odsdywgzyqfu">
                      <input type="text" aria-label={`Group ${gi + 1} point ${pi + 1}`} value={point} onChange={e => {
                const groups = next.groups.slice();
                const points = groups[gi].points.slice();
                points[pi] = e.target.value;
                groups[gi] = {
                  ...groups[gi],
                  points
                };
                editGroups(groups);
              }} placeholder="One point" className={FIELD} data-id="element-1749" data-mp="1ofljg8fvj4m9" />
                    </div>
                    <RowRemove label={`Remove point ${pi + 1}`} onClick={() => {
              const groups = next.groups.slice();
              groups[gi] = {
                ...groups[gi],
                points: groups[gi].points.filter((_, at) => at !== pi)
              };
              editGroups(groups);
            }} data-id="element-1750" data-mp="1tc28d4uwdn0e" />
                  </div>)}

                <button type="button" onClick={() => {
            const groups = next.groups.slice();
            groups[gi] = {
              ...groups[gi],
              points: [...groups[gi].points, '']
            };
            editGroups(groups);
          }} className={`${BUTTON_QUIET} mt-6`} data-id="element-1751" data-mp="33a1weuz6rr1v">
                  Add a point
                </button>
              </div>)}

            <button type="button" onClick={() => editGroups([...next.groups, {
          title: '',
          points: []
        }])} className={`${BUTTON_GHOST} mt-6`} data-id="element-1752" data-mp="3r0pkapphyra2">
              Add a group
            </button>
          </div>}
      </Block>

      <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-4 border-t border-ruleDark pt-8" data-id="element-1753" data-mp="3twzvd59zffr">
        <button type="button" onClick={save} disabled={saving} className={BUTTON_SOLID} data-id="element-1754" data-mp="1zwrx0t0b52ea">
          {saving ? 'Saving…' : 'Save Wrap-Up'}
        </button>
        {dirty && <p className="label m-0 text-paper/40" data-id="element-1755" data-mp="29ppj194id9hn">Unsaved changes</p>}
        <span className="flex-grow" data-id="element-1756" data-mp="24xpet4henuf9" />
        <button type="button" onClick={() => setConfirmDiscard(true)} className="label text-paper/25 transition-colors duration-150 ease-out hover:text-paper/70" data-id="element-1757" data-mp="24wo2kwonl1vy">
          Remove Wrap-Up
        </button>
      </div>

      {confirmDiscard && <div className="mt-8 max-w-xl border border-paper/30 px-6 py-6" data-id="element-1758" data-mp="2smqo0ty2b9ll">
          <p className="font-display text-2xl leading-snug" data-id="element-1759" data-mp="2no74t1ausbwx">Remove this wrap-up?</p>
          <p className="mt-4 text-sm leading-relaxed text-paper/40" data-id="element-1760" data-mp="y9sg2gzu4p1f">
            Everything written here is deleted and the client page goes back to films and files
            only. The films themselves are untouched.
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-6" data-id="element-1761" data-mp="34z2jybcxhhhc">
            <button type="button" onClick={discard} disabled={saving} className={BUTTON_SOLID} data-id="element-1762" data-mp="39wuoc4urew2f">
              Remove Wrap-Up
            </button>
            <button type="button" onClick={() => setConfirmDiscard(false)} className={BUTTON_QUIET} data-id="element-1763" data-mp="2w4xw80t3cm61">
              Cancel
            </button>
          </div>
        </div>}
    </div>;
}